import { get, post, put, patch, del } from 'aws-amplify/api';
import type { DocumentType } from '@aws-amplify/core/internals/utils';
import type {
    User,
    CreateUserPayload,
    UserPaginatedResponse,
    PolicyAcknowledgmentStats,
    ApiResponse,
    BackendPolicy,
    BackendPolicyPaginatedResponse,
    PolicyAssignment,
    PolicyAssignmentResponse,
    PolicyAssignmentPaginatedResponse,
    AcknowledgmentView,
    ManualReminderResult,
    BulkReminderResult,
    BulkRevokeResult,
    AuditPaginatedResponse,
    AuditQueryParams,
    SignerPortalResponse,
    DashboardAnalytics,
    GroupCompletionStats,
    AnalyticsDateRange,
    Group,
    GroupWithMembers,
    GroupPolicySummary,
    AddMembersResult,
    BulkUploadRow,
    BulkCreateUsersResponse,
    ReassignmentCandidatesResponse,
    ReassignResult,
    SupportTicket,
    SupportTicketMessage,
    SupportTicketStatus,
    SupportTicketWithMessages,
    CreateSupportTicket,
    CreateSupportTicketMessage,
} from '../types';
import type {
    AuditorLinkSummary,
    CreateAuditorLinkRequest,
    CreateAuditorLinkResponse,
} from '@policy-tracker/types';

const API_NAME = 'PolicyTracker';

/** Maximum number of pages to fetch in any paginated loop to prevent infinite loops. */
const MAX_PAGES = 100;

/**
 * Minimal shape of an Amplify REST response — wide enough that any `get`/`post`/
 * `put`/`del` operation result fits, without pulling Amplify's internal
 * `RestApiResponse` type into every public service signature.
 */
interface ApiResponseLike {
    body: { json: () => Promise<unknown> };
}

/**
 * Reads the JSON body off a resolved Amplify REST response and casts to the
 * caller's expected `T`. The cast is unchecked — callers are responsible for
 * shaping the request such that the response body actually matches `T`.
 */
async function handleApiResponse<T>(response: ApiResponseLike): Promise<T> {
    const data = await response.body.json();
    return data as T;
}

/**
 * Helper that round-trips a payload through JSON so any non-JSON values
 * (`Date`, `undefined`, functions) are normalized before sending to Amplify.
 * Returns Amplify's `DocumentType` — the same type Amplify accepts on its
 * `body: DocumentType | FormData` slot — so callers can drop the result in
 * directly without an unsafe cast.
 */
function serializeForApi<T>(obj: T): DocumentType {
    return JSON.parse(JSON.stringify(obj)) as DocumentType;
}

export class PolicyService {
    static async getPolicies(options?: { limit?: number; nextToken?: string; tags?: string[] }): Promise<BackendPolicyPaginatedResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (options?.limit) queryParams.append('limit', options.limit.toString());
            if (options?.nextToken) queryParams.append('nextToken', options.nextToken);
            if (options?.tags && options.tags.length > 0) queryParams.append('tags', options.tags.join(','));

            const queryString = queryParams.toString();
            const path = queryString ? `/policies?${queryString}` : '/policies';

            const restOperation = get({
                apiName: API_NAME,
                path,
            });
            const response = await restOperation.response;
            return await handleApiResponse<BackendPolicyPaginatedResponse>(response);
        } catch (error) {
            console.error('Error fetching policies:', error);
            throw error;
        }
    }

    static async getAllPolicies(options?: { tags?: string[] }): Promise<BackendPolicy[]> {
        const allItems: BackendPolicy[] = [];
        let nextToken: string | undefined;
        let pages = 0;
        do {
            const result = await PolicyService.getPolicies({ ...(nextToken ? { nextToken } : {}), ...(options?.tags ? { tags: options.tags } : {}) });
            allItems.push(...result.items);
            nextToken = result.hasMore ? result.nextToken : undefined;
            pages++;
        } while (nextToken && pages < MAX_PAGES);
        return allItems;
    }

    static async getPolicy(id: string): Promise<BackendPolicy> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/policies/${id}`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<BackendPolicy>(response);
        } catch (error) {
            console.error('Error fetching policy:', error);
            throw error;
        }
    }

    /**
     * Fetch the policies attached to a group (#2882) — the auto-assignment
     * set shown in the group detail dialog's "Assigned Policies" section.
     * Backed by `GET /api/groups/:groupId/policies` on the policy service
     * (group CRUD itself lives in the user service). Admin only.
     */
    static async getPoliciesForGroup(groupId: string): Promise<GroupPolicySummary[]> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/groups/${groupId}/policies`,
            });
            const response = await restOperation.response;
            const data = await handleApiResponse<{ policies: GroupPolicySummary[] }>(response);
            return data.policies;
        } catch (error) {
            console.error('Error fetching policies for group:', error);
            throw error;
        }
    }

    /**
     * Updates mutable fields on an existing policy (title, recurrenceType, tags).
     * @param id - The policy ID to update.
     * @param data - Partial update payload (all fields optional).
     */
    static async updatePolicy(
        id: string,
        data: { title?: string; recurrenceType?: 'annual' | 'quarterly' | 'semi-annual' | null; tags?: string[] }
    ): Promise<BackendPolicy> {
        try {
            const restOperation = put({
                apiName: API_NAME,
                path: `/policies/${id}`,
                options: {
                    body: serializeForApi(data),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<BackendPolicy>(response);
        } catch (error) {
            console.error('Error updating policy:', error);
            throw error;
        }
    }

    static async createPolicy(title: string, file: File, recurrenceType?: string, tags?: string[]): Promise<BackendPolicy> {
        try {
            const formData = new FormData();
            formData.append('title', title);
            formData.append('file', file);
            if (recurrenceType) {
                formData.append('recurrenceType', recurrenceType);
            }
            if (tags && tags.length > 0) {
                formData.append('tags', tags.join(','));
            }

            const restOperation = post({
                apiName: API_NAME,
                path: '/policies',
                options: {
                    body: formData,
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<BackendPolicy>(response);
        } catch (error) {
            console.error('Error creating policy:', error);
            throw error;
        }
    }

    /**
     * Request an AI-suggested title for a PDF before creating the policy
     * (#2772, bulk upload). Admin only; Standard+ with AI processing enabled.
     * Throws on any failure — callers fall back to the filename-derived title
     * and never block the upload flow on this call.
     */
    static async suggestPolicyTitle(file: File): Promise<{ title: string }> {
        const formData = new FormData();
        formData.append('file', file);

        const restOperation = post({
            apiName: API_NAME,
            path: '/policies/suggest-title',
            options: {
                body: formData,
                // #2910: disable Amplify's default jittered-exponential-backoff
                // retry budget (3 attempts on 5xx). The fallback is free — the
                // filename-derived title is already rendered — so a retry only
                // delays batch settle and triples Lambda invocations (each
                // re-running multipart parse + PDF text extraction).
                retryStrategy: { strategy: 'no-retry' },
            },
        });
        const response = await restOperation.response;
        return await handleApiResponse<{ title: string }>(response);
    }

    static async assignPolicy(policyId: string, userIds: string[], dueDate?: string, groupIds?: string[]): Promise<PolicyAssignmentResponse> {
        try {
            const body: { userIds: string[]; dueDate?: string; groupIds?: string[] } = { userIds };
            if (dueDate) {
                body.dueDate = dueDate;
            }
            if (groupIds && groupIds.length > 0) {
                body.groupIds = groupIds;
            }
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/assignments`,
                options: {
                    body: serializeForApi(body),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<PolicyAssignmentResponse>(response);
        } catch (error) {
            console.error('Error assigning policy:', error);
            throw error;
        }
    }

    /**
     * Best-effort capture of the browser's IANA time zone (#2913), e.g.
     * `America/Chicago`. Sent with sign/decline calls so emails can render
     * timestamps in the signer's local time. Returns `undefined` (and never
     * throws) when the environment can't resolve a zone — the backend then
     * falls back to explicitly-labeled UTC.
     */
    private static resolveBrowserTimezone(): string | undefined {
        try {
            const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
            return zone || undefined;
        } catch {
            return undefined;
        }
    }

    /**
     * Acknowledges a policy assignment. The signer MUST affirmatively consent
     * to electronic signature — the body fields are required (issue #1145).
     * The browser time zone rides along when resolvable (#2913).
     */
    static async acknowledgePolicy(
        policyId: string,
        assignmentId: string | undefined,
        electronicSignatureConsented: true,
        consentVersion: string
    ): Promise<{ message: string; policyTitle: string }> {
        try {
            const queryString = assignmentId ? `?assignmentId=${encodeURIComponent(assignmentId)}` : '';
            const timezone = PolicyService.resolveBrowserTimezone();
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/acknowledgments${queryString}`,
                options: {
                    body: { electronicSignatureConsented, consentVersion, ...(timezone && { timezone }) },
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ message: string; policyTitle: string }>(response);
        } catch (error) {
            console.error('Error acknowledging policy:', error);
            throw error;
        }
    }

    /**
     * Declines a policy assignment for the authenticated signer (issue #1146).
     *
     * `reason` is optional — declines without a reason are accepted and the
     * server enforces only the 500-character cap. `assignmentId` disambiguates
     * across recurrence periods when present.
     */
    static async declinePolicy(
        policyId: string,
        assignmentId: string | undefined,
        reason?: string
    ): Promise<{ message: string; policyTitle: string; assignmentId: string }> {
        try {
            const queryString = assignmentId ? `?assignmentId=${encodeURIComponent(assignmentId)}` : '';
            const body: { reason?: string; timezone?: string } = {};
            if (reason !== undefined && reason.length > 0) body.reason = reason;
            // #2913 — best-effort browser zone capture, mirrors acknowledgePolicy.
            const timezone = PolicyService.resolveBrowserTimezone();
            if (timezone) body.timezone = timezone;
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/decline${queryString}`,
                options: { body },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ message: string; policyTitle: string; assignmentId: string }>(response);
        } catch (error) {
            console.error('Error declining policy:', error);
            throw error;
        }
    }

    static async getAcknowledgments(policyId: string): Promise<AcknowledgmentView> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/policies/${policyId}/acknowledgments`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<AcknowledgmentView>(response);
        } catch (error) {
            console.error('Error fetching acknowledgments:', error);
            throw error;
        }
    }

    static async sendReminder(policyId: string, userId: string): Promise<ManualReminderResult> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/acknowledgments/${userId}/remind`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<ManualReminderResult>(response);
        } catch (error) {
            console.error('Error sending reminder:', error);
            throw error;
        }
    }

    static async remindAllPending(policyId: string): Promise<BulkReminderResult> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/remind-pending`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<BulkReminderResult>(response);
        } catch (error) {
            console.error('Error sending bulk reminders:', error);
            throw error;
        }
    }

    /**
     * Fetches all assignments for a policy by paginating until `hasMore` is false.
     * Stops after MAX_PAGES pages to prevent infinite loops.
     * @param policyId - The policy to fetch assignments for
     */
    static async getAllAssignments(policyId: string): Promise<PolicyAssignment[]> {
        const allItems: PolicyAssignment[] = [];
        let nextToken: string | undefined;
        let pages = 0;
        do {
            const result = await PolicyService.getAssignments(policyId, nextToken ? { nextToken } : undefined);
            allItems.push(...result.items);
            nextToken = result.hasMore ? result.nextToken : undefined;
            pages++;
        } while (nextToken && pages < MAX_PAGES);
        return allItems;
    }

    /**
     * Fetches acknowledgment views for all policies in the tenant in a single API call.
     * Replaces the N+1 pattern of calling getAcknowledgments for each policy individually.
     */
    static async getAllAcknowledgments(): Promise<(AcknowledgmentView & { policyTitle: string })[]> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/acknowledgments',
            });
            const response = await restOperation.response;
            return await handleApiResponse<(AcknowledgmentView & { policyTitle: string })[]>(response);
        } catch (error) {
            console.error('Error fetching all acknowledgments:', error);
            throw error;
        }
    }

    /**
     * Fetches all assignments across all policies for the tenant in a single API call.
     * When dueDateFrom and/or dueDateTo are provided, only assignments whose dueDate
     * falls within the given range are returned.
     *
     * @param options.dueDateFrom - Lower bound YYYY-MM-DD (inclusive)
     * @param options.dueDateTo - Upper bound YYYY-MM-DD (inclusive)
     */
    static async getAllAssignmentsForTenant(options?: { dueDateFrom?: string; dueDateTo?: string }): Promise<PolicyAssignment[]> {
        try {
            const queryParams = new URLSearchParams();
            if (options?.dueDateFrom) queryParams.append('dueDateFrom', options.dueDateFrom);
            if (options?.dueDateTo) queryParams.append('dueDateTo', options.dueDateTo);

            const queryString = queryParams.toString();
            const path = queryString ? `/assignments?${queryString}` : '/assignments';

            const restOperation = get({
                apiName: API_NAME,
                path,
            });
            const response = await restOperation.response;
            return await handleApiResponse<PolicyAssignment[]>(response);
        } catch (error) {
            console.error('Error fetching all assignments:', error);
            throw error;
        }
    }

    static async getAssignments(policyId: string, options?: { limit?: number; nextToken?: string }): Promise<PolicyAssignmentPaginatedResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (options?.limit) queryParams.append('limit', options.limit.toString());
            if (options?.nextToken) queryParams.append('nextToken', options.nextToken);

            const queryString = queryParams.toString();
            const path = queryString ? `/policies/${policyId}/assignments?${queryString}` : `/policies/${policyId}/assignments`;

            const restOperation = get({
                apiName: API_NAME,
                path,
            });
            const response = await restOperation.response;
            return await handleApiResponse<PolicyAssignmentPaginatedResponse>(response);
        } catch (error) {
            console.error('Error fetching assignments:', error);
            throw error;
        }
    }

    /**
     * Revokes a single pending assignment.
     * @param assignmentId - The ID of the assignment to revoke.
     * @param reason - Optional reason for the revocation.
     */
    static async revokeAssignment(assignmentId: string, reason?: string): Promise<PolicyAssignment> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: `/assignments/${assignmentId}/revoke`,
                options: { body: serializeForApi({ reason }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<PolicyAssignment>(response);
        } catch (error) {
            console.error('Error revoking assignment:', error);
            throw error;
        }
    }

    /**
     * Edits the due date on a pending assignment (#2376).
     *
     * Calls `PATCH /api/assignments/:assignmentId` with `{ dueDate }`. Pass a
     * `YYYY-MM-DD` string to set a new date, or `null` to clear the due date.
     *
     * Errors propagate to the caller (Acknowledgments handles the 401/403/404/
     * 409 mapping). The server-side validation enforces:
     *   - assignment must be in pending status (else 409),
     *   - date must be today-or-later when non-null (else 400),
     *   - assignment must belong to the caller's tenant (else 404).
     */
    static async editAssignmentDueDate(assignmentId: string, dueDate: string | null): Promise<PolicyAssignment> {
        try {
            const restOperation = patch({
                apiName: API_NAME,
                path: `/assignments/${assignmentId}`,
                options: { body: serializeForApi({ dueDate }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<PolicyAssignment>(response);
        } catch (error) {
            console.error('Error editing assignment due date:', error);
            throw error;
        }
    }

    /**
     * Exports the acknowledgment audit log for a specific policy as a CSV file download.
     * Calls GET /api/policies/:policyId/acknowledgments/export and triggers a browser file download.
     * @param policyId - The policy to export acknowledgments for.
     */
    static async exportAcknowledgmentsCsv(policyId: string): Promise<void> {
        const restOperation = get({
            apiName: API_NAME,
            path: `/policies/${policyId}/acknowledgments/export`,
        });
        const response = await restOperation.response;
        const csvContent = await response.body.text();

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `policy-audit-${policyId}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    /**
     * Downloads the Certificate of Completion / Decline PDF for an
     * assignment (issue #1148) and triggers a browser download. Calls
     * `GET /api/assignments/:id/certificate` and reads the response body
     * as a Blob.
     *
     * @param assignmentId - The assignment whose certificate to download.
     * @param filename - Optional override for the saved filename. When
     *   omitted, falls back to `certificate-<assignmentId>-<YYYY-MM-DD>.pdf`.
     *   The server already returns a sensible filename in
     *   `Content-Disposition`, but Amplify's REST helper does not surface
     *   that header to the caller, so the client picks one explicitly.
     */
    static async downloadCertificate(assignmentId: string, filename?: string): Promise<void> {
        const restOperation = get({
            apiName: API_NAME,
            path: `/assignments/${assignmentId}/certificate`,
        });
        const response = await restOperation.response;
        const blob = await response.body.blob();

        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename ?? `certificate-${assignmentId}-${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    /**
     * Bulk-revokes multiple pending assignments.
     * @param assignmentIds - Array of assignment IDs to revoke.
     * @param reason - Optional reason for revocation.
     */
    static async bulkRevokeAssignments(assignmentIds: string[], reason?: string): Promise<BulkRevokeResult> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/assignments/bulk-revoke',
                options: { body: serializeForApi({ assignmentIds, reason }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<BulkRevokeResult>(response);
        } catch (error) {
            console.error('Error bulk revoking assignments:', error);
            throw error;
        }
    }

    /**
     * Uploads a new document version for an existing policy.
     * @param policyId - The policy to upload a new version for.
     * @param file - The new PDF file.
     * @param changeNote - Optional description of what changed.
     */
    static async uploadNewVersion(policyId: string, file: File, changeNote?: string): Promise<BackendPolicy> {
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (changeNote) {
                formData.append('changeNote', changeNote);
            }

            // Single fetchAuthSession() call — two separate calls could straddle a
            // token refresh and yield `Bearer undefined` (#2762). Omit the header
            // entirely when no token is available rather than sending an empty value.
            const session = await import('aws-amplify/auth').then((m) => m.fetchAuthSession());
            const idToken = session.tokens?.idToken?.toString();
            const headers: Record<string, string> = {};
            if (idToken) {
                headers.Authorization = `Bearer ${idToken}`;
            }

            // Raw fetch (not Amplify REST — it can't send FormData multipart bodies),
            // so the path must include the `policy` service segment that the Amplify
            // endpoint (`${origin}/api/policy`, amplify-config.ts) normally provides.
            // The CloudFront function routes /api/<service>/<rest> by the <service>
            // segment; without it the request 502s at CloudFront (#2819).
            const fetchResponse = await fetch(`/api/policy/policies/${policyId}/versions`, {
                method: 'POST',
                body: formData,
                headers,
            });

            if (!fetchResponse.ok) {
                throw new Error(`Upload failed: ${fetchResponse.status}`);
            }

            return (await fetchResponse.json()) as BackendPolicy;
        } catch (error) {
            console.error('Error uploading new policy version:', error);
            throw error;
        }
    }

    /**
     * Fetches the version history for a policy.
     * @param policyId - The policy to fetch version history for.
     */
    static async getVersionHistory(policyId: string): Promise<import('../types').PolicyVersion[]> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/policies/${policyId}/versions`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<import('../types').PolicyVersion[]>(response);
        } catch (error) {
            console.error('Error fetching version history:', error);
            throw error;
        }
    }

    /**
     * Returns a presigned URL for a specific version of a policy document.
     * @param policyId - The policy.
     * @param version - The version number.
     */
    static async getVersionPresignedUrl(policyId: string, version: number): Promise<string> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/policies/${policyId}/versions/${version}`,
            });
            const response = await restOperation.response;
            const data = await handleApiResponse<{ url: string }>(response);
            return data.url;
        } catch (error) {
            console.error('Error fetching version presigned URL:', error);
            throw error;
        }
    }

    /**
     * Returns users who signed a previous version of the policy but have no pending
     * assignment for the current version.
     * @param policyId - The policy to query.
     */
    static async getReassignmentCandidates(policyId: string): Promise<ReassignmentCandidatesResponse> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/policies/${policyId}/reassignment-candidates`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<ReassignmentCandidatesResponse>(response);
        } catch (error) {
            console.error('Error fetching reassignment candidates:', error);
            throw error;
        }
    }

    /**
     * Reassigns a set of users to the current version of a policy.
     * @param policyId - The policy to assign.
     * @param userIds - Array of user IDs to reassign.
     * @param dueDate - Optional due date (YYYY-MM-DD).
     */
    static async reassignPreviousSigners(policyId: string, userIds: string[], dueDate?: string): Promise<ReassignResult> {
        try {
            const body: { userIds: string[]; dueDate?: string } = { userIds };
            if (dueDate) {
                body.dueDate = dueDate;
            }
            const restOperation = post({
                apiName: API_NAME,
                path: `/policies/${policyId}/reassign`,
                options: { body: serializeForApi(body) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<ReassignResult>(response);
        } catch (error) {
            console.error('Error reassigning previous signers:', error);
            throw error;
        }
    }

    /**
     * Permanently deletes a policy by ID. The backend cascades the deletion to
     * all assignments and acknowledgments for the policy.
     * @param id - The policy ID to delete.
     */
    static async deletePolicy(id: string): Promise<void> {
        try {
            const restOperation = del({
                apiName: API_NAME,
                path: `/policies/${id}`,
            });
            await restOperation.response;
        } catch (error) {
            console.error('Error deleting policy:', error);
            throw error;
        }
    }
}

const USER_API_NAME = 'UserService';

export class UserService {
    static async getUsers(options?: { limit?: number; nextToken?: string }): Promise<UserPaginatedResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (options?.limit) queryParams.append('limit', options.limit.toString());
            if (options?.nextToken) queryParams.append('nextToken', options.nextToken);

            const queryString = queryParams.toString();
            const path = queryString ? `/users?${queryString}` : '/users';

            const restOperation = get({
                apiName: USER_API_NAME,
                path,
            });
            const response = await restOperation.response;
            return await handleApiResponse<UserPaginatedResponse>(response);
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    }

    /**
     * Fetches all users by paginating until `hasMore` is false.
     * Stops after MAX_PAGES pages to prevent infinite loops.
     */
    static async getAllUsers(): Promise<User[]> {
        const allItems: User[] = [];
        let nextToken: string | undefined;
        let pages = 0;
        do {
            const result = await UserService.getUsers(nextToken ? { nextToken } : undefined);
            allItems.push(...result.items);
            nextToken = result.hasMore ? result.nextToken : undefined;
            pages++;
        } while (nextToken && pages < MAX_PAGES);
        return allItems;
    }

    static async createUser(user: CreateUserPayload): Promise<User> {
        try {
            const restOperation = post({
                apiName: USER_API_NAME,
                path: '/users',
                options: {
                    body: serializeForApi(user),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error creating user:', error);
            throw error;
        }
    }

    static async getUser(id: string): Promise<User> {
        try {
            const restOperation = get({
                apiName: USER_API_NAME,
                path: `/users/${id}`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error fetching user:', error);
            throw error;
        }
    }

    /**
     * Fetches the signed-in user's own profile (issue #1947).
     *
     * Backed by GET /api/users/me — the user is resolved from the JWT, so
     * no userId is required from the caller. Used by the Settings → Profile
     * panel to populate display-name and email fields.
     */
    static async getMe(): Promise<User> {
        try {
            const restOperation = get({
                apiName: USER_API_NAME,
                path: '/users/me',
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error fetching current user:', error);
            throw error;
        }
    }

    /**
     * Updates the signed-in user's own profile (issue #1947 + #1948).
     *
     * Backed by PATCH /api/users/me. The server-side schema restricts the
     * body to `firstName` / `lastName` / `notificationPreferences`; any
     * other field will reject with a 400. Email and role changes are
     * intentionally NOT available through this endpoint.
     */
    static async updateMe(data: {
        firstName?: string;
        lastName?: string;
        notificationPreferences?: {
            ackDueReminders?: boolean;
            policyAssignments?: boolean;
            weeklyAdminDigest?: boolean;
        };
    }): Promise<User> {
        try {
            const restOperation = patch({
                apiName: USER_API_NAME,
                path: '/users/me',
                options: {
                    body: serializeForApi(data),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error updating current user:', error);
            throw error;
        }
    }

    static async updateUser(id: string, data: { firstName?: string; lastName?: string; email?: string }): Promise<User> {
        try {
            const restOperation = put({
                apiName: USER_API_NAME,
                path: `/users/${id}`,
                options: {
                    body: serializeForApi(data),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error updating user:', error);
            throw error;
        }
    }

    static async deactivateUser(id: string): Promise<User> {
        try {
            const restOperation = put({
                apiName: USER_API_NAME,
                path: `/users/${id}/deactivate`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error deactivating user:', error);
            throw error;
        }
    }

    static async reactivateUser(id: string): Promise<User> {
        try {
            const restOperation = put({
                apiName: USER_API_NAME,
                path: `/users/${id}/reactivate`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error reactivating user:', error);
            throw error;
        }
    }

    /**
     * Triggers a Cognito password reset email for the user (#2253).
     *
     * Drives the bulk-reset action in `Users.tsx` — one call per selected
     * user. The server replies 200 with `{ message: 'Password reset email
     * sent' }` on success and 429 with `{ code: 'TOO_MANY_REQUESTS' }`
     * when Cognito throttles. Returns `Promise<void>`; the response body
     * is intentionally not parsed because the caller (the bulk-reset loop)
     * only cares about resolved-vs-rejected.
     *
     * Errors are rethrown WITHOUT logging the user ID or email — the
     * parent loop in `Users.tsx` decides whether to log (it suppresses
     * 403 and 429 to avoid console noise on expected guard paths).
     */
    static async resetUserPassword(id: string): Promise<void> {
        const restOperation = put({
            apiName: USER_API_NAME,
            path: `/users/${id}/reset-password`,
        });
        await restOperation.response;
    }

    /**
     * Toggles the `isTestUser` flag on a user. Admin-only on the server.
     * Server emits a `UserTestModeChanged` audit event.
     */
    static async setTestMode(id: string, isTestUser: boolean): Promise<User> {
        try {
            const restOperation = patch({
                apiName: USER_API_NAME,
                path: `/users/${id}/test-mode`,
                options: { body: serializeForApi({ isTestUser }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<User>(response);
        } catch (error) {
            console.error('Error setting test mode:', error);
            throw error;
        }
    }

    /**
     * Bulk-creates users from an array of row data.
     * @param rows - Array of user rows (max 100).
     * @returns Successes, failures, and totalProcessed count.
     */
    static async bulkCreateUsers(rows: BulkUploadRow[]): Promise<BulkCreateUsersResponse> {
        try {
            const restOperation = post({
                apiName: USER_API_NAME,
                path: '/users/bulk',
                options: {
                    body: serializeForApi({ users: rows }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<BulkCreateUsersResponse>(response);
        } catch (error) {
            console.error('Error bulk creating users:', error);
            throw error;
        }
    }

    /**
     * Permanently deletes a user by ID.
     * @param id - The user ID to delete.
     */
    static async deleteUser(id: string): Promise<void> {
        try {
            const restOperation = del({
                apiName: USER_API_NAME,
                path: `/users/${id}`,
            });
            await restOperation.response;
        } catch (error) {
            console.error('Error deleting user:', error);
            throw error;
        }
    }
}

/** Service for managing groups in the User Service API. */
export class GroupService {
    /**
     * Fetch all groups for the current tenant.
     */
    static async getGroups(): Promise<Group[]> {
        try {
            const restOperation = get({
                apiName: USER_API_NAME,
                path: '/groups',
            });
            const response = await restOperation.response;
            return await handleApiResponse<Group[]>(response);
        } catch (error) {
            console.error('Error fetching groups:', error);
            throw error;
        }
    }

    /**
     * Create a new group with the given name.
     * @param name - The display name for the new group.
     */
    static async createGroup(name: string): Promise<Group> {
        try {
            const restOperation = post({
                apiName: USER_API_NAME,
                path: '/groups',
                options: {
                    body: serializeForApi({ name }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<Group>(response);
        } catch (error) {
            console.error('Error creating group:', error);
            throw error;
        }
    }

    /**
     * Fetch a single group with its member user IDs.
     * @param id - The group ID.
     */
    static async getGroup(id: string): Promise<GroupWithMembers> {
        try {
            const restOperation = get({
                apiName: USER_API_NAME,
                path: `/groups/${id}`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<GroupWithMembers>(response);
        } catch (error) {
            console.error('Error fetching group:', error);
            throw error;
        }
    }

    /**
     * Rename a group.
     * @param id - The group ID.
     * @param name - The new name.
     */
    static async renameGroup(id: string, name: string): Promise<Group> {
        try {
            const restOperation = put({
                apiName: USER_API_NAME,
                path: `/groups/${id}`,
                options: {
                    body: serializeForApi({ name }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<Group>(response);
        } catch (error) {
            console.error('Error renaming group:', error);
            throw error;
        }
    }

    /**
     * Delete a group and all its memberships.
     * @param id - The group ID.
     */
    static async deleteGroup(id: string): Promise<void> {
        try {
            const restOperation = del({
                apiName: USER_API_NAME,
                path: `/groups/${id}`,
            });
            await restOperation.response;
        } catch (error) {
            console.error('Error deleting group:', error);
            throw error;
        }
    }

    /**
     * Fetch member user IDs for a group.
     * @param id - The group ID.
     */
    static async getGroupMembers(id: string): Promise<string[]> {
        try {
            const restOperation = get({
                apiName: USER_API_NAME,
                path: `/groups/${id}/members`,
            });
            const response = await restOperation.response;
            return await handleApiResponse<string[]>(response);
        } catch (error) {
            console.error('Error fetching group members:', error);
            throw error;
        }
    }

    /**
     * Add members to a group.
     * @param id - The group ID.
     * @param userIds - Array of user IDs to add.
     */
    static async addGroupMembers(id: string, userIds: string[]): Promise<AddMembersResult> {
        try {
            const restOperation = post({
                apiName: USER_API_NAME,
                path: `/groups/${id}/members`,
                options: {
                    body: serializeForApi({ userIds }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<AddMembersResult>(response);
        } catch (error) {
            console.error('Error adding group members:', error);
            throw error;
        }
    }

    /**
     * Remove members from a group.
     * @param id - The group ID.
     * @param userIds - Array of user IDs to remove.
     */
    static async removeGroupMembers(id: string, userIds: string[]): Promise<AddMembersResult> {
        try {
            const restOperation = del({
                apiName: USER_API_NAME,
                path: `/groups/${id}/members`,
                options: {
                    body: serializeForApi({ userIds }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<AddMembersResult>(response);
        } catch (error) {
            console.error('Error removing group members:', error);
            throw error;
        }
    }
}

/** Entitlements response shape returned by GET /api/entitlements. */
export interface EntitlementsResponse {
    tier: 'free' | 'standard' | 'premium';
    features: {
        maxAcknowledgmentsPerPeriod: number | null;
        maxActivePolicies: number | null;
        maxAdminUsers: number | null;
        /** Maximum total users allowed for the tenant. Null means unlimited. */
        maxTotalUsers: number | null;
        groupsEnabled: boolean;
        recurrenceEnabled: boolean;
        ssoEnabled: boolean;
        scheduledRemindersEnabled: boolean;
        analyticsEnabled: boolean;
        auditLogEnabled: boolean;
        dataRetentionDays: number | null;
        accountIsolation: boolean;
        adminDigestEnabled: boolean;
        templatesEnabled: boolean;
        /** Whether policy document versioning is enabled. */
        versioningEnabled: boolean;
        /** Whether SCIM 2.0 bearer-token provisioning is enabled (#2240).
         *  Optional because pre-#2240 entitlement records and the dozens
         *  of pre-existing UI test fixtures don't carry it; callers must
         *  gate on explicit `=== true`. */
        scimEnabled?: boolean;
    };
    /** Number of acknowledgments recorded in the current billing period. */
    currentPeriodAcknowledgments: number;
    /**
     * The `YYYY-MM-DD` anchor date for the current billing period. Incremented
     * annually on the tenant's signup anniversary. Not always present — older
     * tenant records missing entitlements fall back to unlimited features with
     * no period date.
     */
    periodStartDate?: string;
    /**
     * Number of "User Pack" add-ons purchased by the tenant (#1630). Each pack
     * lifts `features.maxTotalUsers` by 50 — the effective cap is already baked
     * into `maxTotalUsers` by the webhook handler (#1878). The UI surfaces the
     * raw pack quantity so the Plan & Usage panel can render the breakdown
     * "200 base + N packs × 50 = X effective cap". Optional — absent for
     * non-Standard tenants or tenants who haven't purchased any packs.
     */
    userPackQuantity?: number;
    /**
     * Whether the tenant has an established billing account (Stripe customer)
     * in the TM account (#2070). The UI gates the Manage Billing button on
     * `tier === 'standard' && hasBillingAccount === true` so legacy tenants
     * without a Stripe customer don't see a button that 404s on click.
     * Optional — absent for tenants whose entitlements row pre-dates this
     * field (treated as `false` by the hook).
     */
    hasBillingAccount?: boolean;
}

/** Service for retrieving tenant feature entitlements from the policy service. */
export class EntitlementsApiService {
    /**
     * Fetch the current tenant's tier and feature flags.
     * Returns { tier, features } where features contains all feature-gate booleans.
     */
    static async getEntitlements(): Promise<EntitlementsResponse> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/entitlements',
            });
            const response = await restOperation.response;
            return await handleApiResponse<EntitlementsResponse>(response);
        } catch (error) {
            console.error('Error fetching entitlements:', error);
            throw error;
        }
    }
}

const AUDIT_API_NAME = 'AuditService';

/** Service for fetching and exporting the admin activity audit log. */
export class AuditService {
    /**
     * Fetch a page of audit records with optional filters.
     */
    static async getAuditRecords(params?: AuditQueryParams): Promise<AuditPaginatedResponse> {
        try {
            const queryParams = new URLSearchParams();
            if (params?.limit) queryParams.append('limit', params.limit.toString());
            if (params?.nextToken) queryParams.append('nextToken', params.nextToken);
            if (params?.actorEmail) queryParams.append('actorEmail', params.actorEmail);
            if (params?.action) queryParams.append('action', params.action);
            if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
            if (params?.source) queryParams.append('source', params.source);
            if (params?.startDate) queryParams.append('startDate', params.startDate);
            if (params?.endDate) queryParams.append('endDate', params.endDate);

            const queryString = queryParams.toString();
            const path = queryString ? `/audit?${queryString}` : '/audit';

            const restOperation = get({
                apiName: AUDIT_API_NAME,
                path,
            });
            const response = await restOperation.response;
            return await handleApiResponse<AuditPaginatedResponse>(response);
        } catch (error) {
            console.error('Error fetching audit records:', error);
            throw error;
        }
    }

    /**
     * Export audit records as a CSV file download using the backend export endpoint.
     * The backend handles formula injection protection and CSV escaping.
     */
    static async exportAuditCsv(params?: Omit<AuditQueryParams, 'limit' | 'nextToken'>): Promise<void> {
        const queryParams = new URLSearchParams();
        if (params?.actorEmail) queryParams.append('actorEmail', params.actorEmail);
        if (params?.action) queryParams.append('action', params.action);
        if (params?.resourceType) queryParams.append('resourceType', params.resourceType);
        if (params?.source) queryParams.append('source', params.source);
        if (params?.startDate) queryParams.append('startDate', params.startDate);
        if (params?.endDate) queryParams.append('endDate', params.endDate);

        const queryString = queryParams.toString();
        const path = queryString ? `/audit/export?${queryString}` : '/audit/export';

        const restOperation = get({
            apiName: AUDIT_API_NAME,
            path,
        });
        const response = await restOperation.response;
        const csvContent = await response.body.text();

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `activity-log-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
}

/** Service for the signer self-service portal — accessible to authenticated signers and admins. */
export class SignerService {
    /**
     * Fetch all assignments for the currently authenticated signer, split into pending and completed.
     */
    static async getAssignments(): Promise<SignerPortalResponse> {
        const restOperation = get({
            apiName: API_NAME,
            path: '/signer/assignments',
        });
        const response = await restOperation.response;
        return await handleApiResponse<SignerPortalResponse>(response);
    }
}

/** Response shape returned by GET /api/settings. */
export interface SettingsResponse {
    /** The tenant's current digest email frequency. */
    digestFrequency: 'daily' | 'weekly' | 'disabled';
    /** Whether the admin digest frequency is configurable for this tenant's plan. */
    digestFrequencyConfigurable: boolean;
    /**
     * Whether AI processing is enabled for the tenant (#2772). Defaults ON;
     * admins can opt out. When false (or not configurable), bulk upload uses
     * filename-derived titles and no document content is sent to Bedrock.
     */
    aiProcessingEnabled: boolean;
    /** Whether AI features are available on this tenant's plan (Standard+). */
    aiProcessingConfigurable: boolean;
}

/**
 * SAML metadata discriminated union (#1983, extended in #2249). URL mode lets
 * Cognito fetch the metadata XML at provisioning time; XML mode supplies the
 * document inline (used for IdPs without a public metadata URL such as Google
 * Workspace).
 */
export type SsoSamlMetadata = { source: 'url'; metadataUrl: string } | { source: 'xml'; metadataXml: string };

/** Response shape returned by GET /api/sso (#1983, #2249, #2298). */
export interface SsoConfigResponse {
    /** Whether the tenant's plan permits SSO configuration. */
    ssoEnabled: boolean;
    /** The currently-configured SAML metadata, or null when SSO is not provisioned. */
    samlMetadata: SsoSamlMetadata | null;
    /**
     * Whether the tenant has SSO-only enforcement enabled — when true the
     * LoginPage auto-redirects to the IdP on mount and hides the
     * password/magic-link form (#2298). Optional on the wire so pre-#2298
     * deployments still parse; readers should treat `undefined` as `false`.
     */
    ssoOnly?: boolean;
}

/**
 * Service for reading and updating the tenant's SAML SSO configuration
 * (#1983, #2249). Admin-only on the server side; the UI also gates on the
 * `ssoEnabled` entitlement so non-Standard tenants never see a writeable form.
 */
export class SsoApiService {
    /** Fetch the current SSO configuration. */
    static async getConfig(): Promise<SsoConfigResponse> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/sso',
            });
            const response = await restOperation.response;
            return await handleApiResponse<SsoConfigResponse>(response);
        } catch (error) {
            console.error('Error fetching SSO configuration:', error);
            throw error;
        }
    }

    /**
     * Update the tenant's SSO configuration. Sends a wrapper body
     * `{ samlMetadata?, ssoOnly? }`; at least one field must be provided
     * (#2298).
     *
     * - Passing `samlMetadata` triggers an asynchronous CloudFormation
     *   UpdateStack on the tenant's stack; the server returns 202 immediately
     *   and the IdP becomes usable a minute or two later.
     * - Passing `ssoOnly` toggles the SSO-only enforcement flag on the tenant
     *   row; no CFN update fires for this path. The server rejects
     *   `ssoOnly: true` when no SAML metadata is configured on the row and
     *   none is supplied in the body.
     *
     * @param samlMetadata Discriminated union for SAML metadata, or `null`
     *   to skip the metadata field entirely (ssoOnly-only toggle).
     * @param ssoOnly Optional. When provided, persists the SSO-only flag.
     */
    static async setConfig(
        samlMetadata: SsoSamlMetadata | null,
        ssoOnly?: boolean
    ): Promise<{ samlMetadata: SsoSamlMetadata | null; ssoOnly: boolean | null; warnings?: string[] }> {
        try {
            const requestBody: Record<string, unknown> = {};
            if (samlMetadata !== null) {
                requestBody.samlMetadata = samlMetadata;
            }
            if (ssoOnly !== undefined) {
                requestBody.ssoOnly = ssoOnly;
            }

            const restOperation = put({
                apiName: API_NAME,
                path: '/sso',
                options: {
                    body: serializeForApi(requestBody),
                },
            });
            const response = await restOperation.response;
            // `warnings` (#2589) is a non-blocking advisory array — e.g. the
            // submitted IdP metadata advertises an emailAddress NameID format.
            // It never represents an error; the config is still saved.
            return await handleApiResponse<{
                samlMetadata: SsoSamlMetadata | null;
                ssoOnly: boolean | null;
                warnings?: string[];
            }>(response);
        } catch (error) {
            console.error('Error updating SSO configuration:', error);
            throw error;
        }
    }

    /**
     * Clear the tenant's SAML configuration. Triggers an asynchronous
     * CloudFormation UpdateStack reverting the per-tenant Cognito user pool
     * to the non-SAML layout.
     */
    static async clearConfig(): Promise<void> {
        try {
            const restOperation = del({
                apiName: API_NAME,
                path: '/sso',
            });
            await restOperation.response;
        } catch (error) {
            console.error('Error clearing SSO configuration:', error);
            throw error;
        }
    }
}

/** Settings → SCIM token lifecycle API (#2240).
    Mounted on the user-service Gateway at /api/scim-tokens. */
export interface ScimTokenSummary {
    tokenId: string;
    name: string;
    createdAt: string;
    lastUsedAt?: string;
    revokedAt?: string;
}

export interface CreateScimTokenResponse {
    tokenId: string;
    name: string;
    createdAt: string;
    /** Plaintext token — returned ONLY on this call. */
    token: string;
}

export class ScimTokenApiService {
    /** GET /api/scim-tokens — list this tenant's SCIM tokens (no plaintext, no hash). */
    static async list(): Promise<ScimTokenSummary[]> {
        try {
            const restOperation = get({ apiName: USER_API_NAME, path: '/scim-tokens' });
            const response = await restOperation.response;
            return await handleApiResponse<ScimTokenSummary[]>(response);
        } catch (error) {
            console.error('Error listing SCIM tokens:', error);
            throw error;
        }
    }

    /** POST /api/scim-tokens — mint a new SCIM bearer token. */
    static async create(name: string): Promise<CreateScimTokenResponse> {
        try {
            const restOperation = post({
                apiName: USER_API_NAME,
                path: '/scim-tokens',
                options: { body: serializeForApi({ name }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<CreateScimTokenResponse>(response);
        } catch (error) {
            console.error('Error creating SCIM token:', error);
            throw error;
        }
    }

    /** DELETE /api/scim-tokens/:tokenId — soft-revoke a SCIM token (idempotent). */
    static async revoke(tokenId: string): Promise<void> {
        try {
            const restOperation = del({
                apiName: USER_API_NAME,
                path: `/scim-tokens/${encodeURIComponent(tokenId)}`,
            });
            await restOperation.response;
        } catch (error) {
            console.error('Error revoking SCIM token:', error);
            throw error;
        }
    }
}

/** Service for reading and updating tenant digest settings from the policy service. */
export class SettingsApiService {
    /**
     * Fetch the current tenant's digest frequency settings.
     * Returns { digestFrequency, digestFrequencyConfigurable }.
     */
    static async getSettings(): Promise<SettingsResponse> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/settings',
            });
            const response = await restOperation.response;
            return await handleApiResponse<SettingsResponse>(response);
        } catch (error) {
            console.error('Error fetching settings:', error);
            throw error;
        }
    }

    /**
     * Update the tenant's digest email frequency.
     * Admin only. Returns the updated digestFrequency.
     * @param digestFrequency - One of 'daily', 'weekly', or 'disabled'.
     */
    static async updateSettings(digestFrequency: 'daily' | 'weekly' | 'disabled'): Promise<{ digestFrequency: string }> {
        try {
            const restOperation = put({
                apiName: API_NAME,
                path: '/settings',
                options: {
                    body: serializeForApi({ digestFrequency }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ digestFrequency: string }>(response);
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }

    /**
     * Update the tenant's AI-processing opt-out (#2772). Admin only,
     * Standard+ plans. `false` disables all AI processing (no document
     * content is sent to Bedrock).
     */
    static async updateAiProcessingEnabled(aiProcessingEnabled: boolean): Promise<{ aiProcessingEnabled: boolean }> {
        try {
            const restOperation = put({
                apiName: API_NAME,
                path: '/settings',
                options: {
                    body: serializeForApi({ aiProcessingEnabled }),
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ aiProcessingEnabled: boolean }>(response);
        } catch (error) {
            console.error('Error updating settings:', error);
            throw error;
        }
    }
}

/**
 * Frontend SDK for the billing endpoints (issue #1877).
 *
 * The single available endpoint — `POST /api/billing/checkout` — returns
 * the hosted Stripe Checkout URL. The caller is responsible for the
 * redirect (`window.location.assign(response.url)`).
 */
export class BillingService {
    /**
     * Create a Stripe Checkout session for the Free -> Standard upgrade.
     * Admin only. Returns the hosted-checkout URL.
     */
    static async createCheckoutSession(): Promise<{ url: string }> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/billing/checkout',
                options: {
                    body: serializeForApi({}),
                    // #1892: disable Amplify's default jittered-exponential-backoff
                    // retry budget — billing POSTs are unsafe to retry because the
                    // TM-account Stripe writes mint real customer / session state.
                    // Stripe `idempotencyKey` (set on the TM Lambda) is the
                    // defense-in-depth second layer; this is the first.
                    retryStrategy: { strategy: 'no-retry' },
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ url: string }>(response);
        } catch (error) {
            console.error('Error creating Stripe checkout session:', error);
            throw error;
        }
    }

    /**
     * Create a Stripe Customer Portal session for the authenticated tenant
     * (#1879). Admin only. Returns the hosted-portal URL the browser must
     * be redirected to so the customer can self-serve every post-checkout
     * billing change (payment method, invoices, cancel, pack qty).
     *
     * The portal IS Stripe's audit trail — when portal-driven changes
     * land Stripe fires the corresponding webhooks and the existing
     * webhook handlers reconcile entitlements + emit the appropriate
     * domain audit event.
     *
     * Throws on network failure. The frontend caller is responsible for
     * `window.location.assign(response.url)`.
     */
    static async createBillingPortalSession(): Promise<{ url: string }> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/billing/portal',
                options: {
                    body: serializeForApi({}),
                    // #1892: see createCheckoutSession — billing POSTs must
                    // not auto-retry on transient 5xx.
                    retryStrategy: { strategy: 'no-retry' },
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ url: string }>(response);
        } catch (error) {
            console.error('Error creating Stripe billing portal session:', error);
            throw error;
        }
    }

    /**
     * Reconcile the User Pack subscription item quantity for the current
     * tenant (#1630). Admin only. The `quantity` is the absolute pack count
     * the tenant wants to land at — `0` removes the pack entirely.
     *
     * Returns `{ subscriptionId, quantity }` reflecting Stripe's post-update
     * state. The `customer.subscription.updated` webhook then reconciles
     * the entitlements row asynchronously; callers that need the new
     * `maxTotalUsers` should call `EntitlementsApiService.getEntitlements`
     * (typically via the `refetch()` returned by `useEntitlements`) after
     * a short delay so the webhook has time to propagate.
     */
    static async updateUserPackQuantity(quantity: number): Promise<{ subscriptionId: string; quantity: number }> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/billing/user-pack',
                options: {
                    body: serializeForApi({ quantity }),
                    // #1892: see createCheckoutSession — billing POSTs must
                    // not auto-retry on transient 5xx.
                    retryStrategy: { strategy: 'no-retry' },
                },
            });
            const response = await restOperation.response;
            return await handleApiResponse<{ subscriptionId: string; quantity: number }>(response);
        } catch (error) {
            console.error('Error updating user pack quantity:', error);
            throw error;
        }
    }
}

export class DashboardService {
    /**
     * Fetches aggregated acknowledgment statistics for the dashboard.
     *
     * When `range` is supplied, the response includes prior-period fields
     * (`*PriorPeriod`) used by the dashboard to render per-card deltas.
     * Omitting `range` (or passing `'all'`) returns absolute counts only.
     */
    static async getStats(range?: AnalyticsDateRange): Promise<PolicyAcknowledgmentStats> {
        try {
            const path = range !== undefined ? `/dashboard/stats?range=${encodeURIComponent(range)}` : '/dashboard/stats';
            const restOperation = get({
                apiName: API_NAME,
                path,
            });
            const response = await restOperation.response;
            const data = await handleApiResponse<ApiResponse<PolicyAcknowledgmentStats>>(response);
            return data.data;
        } catch (error) {
            console.error('Error fetching dashboard stats:', error);
            throw error;
        }
    }

    /**
     * Fetches per-group completion statistics for the dashboard.
     */
    static async getGroupStats(): Promise<GroupCompletionStats[]> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/dashboard/stats/groups',
            });
            const response = await restOperation.response;
            const data = await handleApiResponse<ApiResponse<GroupCompletionStats[]>>(response);
            return data.data;
        } catch (error) {
            console.error('Error fetching dashboard group stats:', error);
            throw error;
        }
    }

    /**
     * Fetches aggregated analytics (completion trend, time-to-sign, overdue breakdown)
     * for the given date range.
     *
     * @param range - '7d' | '30d' | '90d' | 'all'. Defaults to '30d'.
     */
    static async getAnalytics(range: AnalyticsDateRange = '30d'): Promise<DashboardAnalytics> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: `/dashboard/analytics?range=${encodeURIComponent(range)}`,
            });
            const response = await restOperation.response;
            const data = await handleApiResponse<ApiResponse<DashboardAnalytics>>(response);
            return data.data;
        } catch (error) {
            console.error('Error fetching dashboard analytics:', error);
            throw error;
        }
    }
}

/** A single assignment template record as returned by the API. */
export interface AssignmentTemplateRecord {
    /** Unique identifier for this template (ULID). */
    templateId: string;
    /** Tenant that owns this template. */
    tenantId: string;
    /** Human-readable name for the template. */
    name: string;
    /** Ordered list of policy IDs included in this template. */
    policyIds: string[];
    /** ISO-8601 timestamp when the template was last applied. */
    lastUsedAt?: string;
    /** ISO-8601 timestamp when the template was created. */
    createdAt: string;
    /** ID of the user who created the template. */
    createdBy: string;
    /** ISO-8601 timestamp when the template was last updated. */
    updatedAt?: string;
}

/** Request body for creating an assignment template. */
export interface CreateAssignmentTemplatePayload {
    name: string;
    policyIds: string[];
}

/** Request body for updating an assignment template. */
export interface UpdateAssignmentTemplatePayload {
    name?: string;
    policyIds?: string[];
}

/** Request body for applying an assignment template. */
export interface ApplyTemplatePayload {
    userIds: string[];
    groupIds?: string[];
    dueDate?: string;
}

/** Response from applying a template. */
export interface ApplyTemplateResponse {
    assignments: PolicyAssignment[];
    warnings: string[];
}

/** Service for managing assignment templates. */
export class TemplateApiService {
    /**
     * Fetches all assignment templates for the current tenant.
     */
    static async getTemplates(): Promise<AssignmentTemplateRecord[]> {
        try {
            const restOperation = get({ apiName: API_NAME, path: '/templates' });
            const response = await restOperation.response;
            return await handleApiResponse<AssignmentTemplateRecord[]>(response);
        } catch (error) {
            console.error('Error fetching templates:', error);
            throw error;
        }
    }

    /**
     * Fetches a single assignment template by ID.
     */
    static async getTemplate(templateId: string): Promise<AssignmentTemplateRecord> {
        try {
            const restOperation = get({ apiName: API_NAME, path: `/templates/${templateId}` });
            const response = await restOperation.response;
            return await handleApiResponse<AssignmentTemplateRecord>(response);
        } catch (error) {
            console.error('Error fetching template:', error);
            throw error;
        }
    }

    /**
     * Creates a new assignment template.
     */
    static async createTemplate(payload: CreateAssignmentTemplatePayload): Promise<{ template: AssignmentTemplateRecord; warnings: string[] }> {
        try {
            const restOperation = post({ apiName: API_NAME, path: '/templates', options: { body: serializeForApi(payload) } });
            const response = await restOperation.response;
            return await handleApiResponse<{ template: AssignmentTemplateRecord; warnings: string[] }>(response);
        } catch (error) {
            console.error('Error creating template:', error);
            throw error;
        }
    }

    /**
     * Updates an existing assignment template.
     */
    static async updateTemplate(templateId: string, payload: UpdateAssignmentTemplatePayload): Promise<{ template: AssignmentTemplateRecord; warnings: string[] }> {
        try {
            const restOperation = put({ apiName: API_NAME, path: `/templates/${templateId}`, options: { body: serializeForApi(payload) } });
            const response = await restOperation.response;
            return await handleApiResponse<{ template: AssignmentTemplateRecord; warnings: string[] }>(response);
        } catch (error) {
            console.error('Error updating template:', error);
            throw error;
        }
    }

    /**
     * Deletes an assignment template.
     */
    static async deleteTemplate(templateId: string): Promise<void> {
        try {
            const restOperation = del({ apiName: API_NAME, path: `/templates/${templateId}` });
            await restOperation.response;
        } catch (error) {
            console.error('Error deleting template:', error);
            throw error;
        }
    }

    /**
     * Applies an assignment template, creating policy assignments for the specified users/groups.
     */
    static async applyTemplate(templateId: string, payload: ApplyTemplatePayload): Promise<ApplyTemplateResponse> {
        try {
            const restOperation = post({ apiName: API_NAME, path: `/templates/${templateId}/apply`, options: { body: serializeForApi(payload) } });
            const response = await restOperation.response;
            return await handleApiResponse<ApplyTemplateResponse>(response);
        } catch (error) {
            console.error('Error applying template:', error);
            throw error;
        }
    }
}

const SUPPORT_API_NAME = 'SupportService';

/** Paginated list response for support tickets. */
export interface SupportTicketListResponse {
    items: SupportTicket[];
    nextToken?: string;
    hasMore: boolean;
}

/**
 * Service for tenant admin support tickets (#2303 part 1) — file, list,
 * view, reply, close, and reopen. All requests hit the sub-account support
 * API gateway under `/api/support/tickets`.
 */
export class SupportService {
    /** List tickets for the current tenant, optionally filtered by status. */
    static async listTickets(status?: SupportTicketStatus): Promise<SupportTicketListResponse> {
        try {
            const path = status ? `/tickets?status=${status}` : '/tickets';
            const restOperation = get({ apiName: SUPPORT_API_NAME, path });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicketListResponse>(response);
        } catch (error) {
            console.error('Error listing support tickets:', error);
            throw error;
        }
    }

    /** Fetch a single ticket with its full message thread. */
    static async getTicket(ticketId: string): Promise<SupportTicketWithMessages> {
        try {
            const restOperation = get({ apiName: SUPPORT_API_NAME, path: `/tickets/${ticketId}` });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicketWithMessages>(response);
        } catch (error) {
            console.error('Error fetching support ticket:', error);
            throw error;
        }
    }

    /** Open a new ticket. The body becomes the first message in the thread. */
    static async createTicket(payload: CreateSupportTicket): Promise<SupportTicketWithMessages> {
        try {
            const restOperation = post({
                apiName: SUPPORT_API_NAME,
                path: '/tickets',
                options: { body: serializeForApi(payload) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicketWithMessages>(response);
        } catch (error) {
            console.error('Error creating support ticket:', error);
            throw error;
        }
    }

    /** Append a reply to an open ticket. Backend rejects with 400 when closed. */
    static async addMessage(ticketId: string, payload: CreateSupportTicketMessage): Promise<SupportTicketMessage> {
        try {
            const restOperation = post({
                apiName: SUPPORT_API_NAME,
                path: `/tickets/${ticketId}/messages`,
                options: { body: serializeForApi(payload) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicketMessage>(response);
        } catch (error) {
            console.error('Error adding support ticket message:', error);
            throw error;
        }
    }

    /** Close a ticket with the supplied reason. */
    static async closeTicket(ticketId: string, closedReason: string): Promise<SupportTicket> {
        try {
            const restOperation = patch({
                apiName: SUPPORT_API_NAME,
                path: `/tickets/${ticketId}`,
                options: { body: serializeForApi({ status: 'closed', closedReason }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicket>(response);
        } catch (error) {
            console.error('Error closing support ticket:', error);
            throw error;
        }
    }

    /** Reopen a closed ticket. */
    static async reopenTicket(ticketId: string): Promise<SupportTicket> {
        try {
            const restOperation = patch({
                apiName: SUPPORT_API_NAME,
                path: `/tickets/${ticketId}`,
                options: { body: serializeForApi({ status: 'open' }) },
            });
            const response = await restOperation.response;
            return await handleApiResponse<SupportTicket>(response);
        } catch (error) {
            console.error('Error reopening support ticket:', error);
            throw error;
        }
    }
}

/** Which rendering of the compliance packet to request (#2767). */
export type CompliancePacketVariant = 'official' | 'sample';

/** Response shape from `POST /api/compliance`. */
export interface CompliancePacketResponse {
    /** 5-minute presigned download URL for the generated PDF. */
    url: string;
    /** The ULID identifying the generated packet (matches the S3 key). */
    packetId: string;
    /**
     * SHA-256 hex of the packet's canonical JSON, printed on the cover.
     * Absent on the `sample` variant (#2767) — the watermarked preview is
     * deliberately not tamper-evident.
     */
    packetHash?: string;
    /** True when the server rendered the watermarked sample variant (#2767). */
    sample?: boolean;
}

/**
 * Typed error surfaced when the server rejects a compliance-packet request
 * because the tenant is below the Standard tier (HTTP 403 with
 * `{ error: 'standard_tier_required' }`). The UI catches this to render the
 * inline upgrade prompt rather than a generic failure message (#2495).
 */
export class StandardTierRequiredError extends Error {
    constructor(message = 'Compliance packet export requires the Standard plan.') {
        super(message);
        this.name = 'StandardTierRequiredError';
    }
}

/**
 * Reads the HTTP status code off an Amplify REST error, when present. Amplify
 * v6 rejects `post`/`get` with an error carrying a `.response.statusCode`.
 * Returns `undefined` when the shape is not recognized.
 */
function statusCodeOf(error: unknown): number | undefined {
    const response = (error as { response?: { statusCode?: number } })?.response;
    return typeof response?.statusCode === 'number' ? response.statusCode : undefined;
}

/**
 * Settings → Compliance API (#2495). Admin-only on the server; Standard or
 * Premium tier required.
 */
export class CompliancePacketApiService {
    /** Re-export so the UI layer can `instanceof`-check without importing twice. */
    static readonly StandardTierRequiredError = StandardTierRequiredError;

    /**
     * POST /api/compliance — generate a fresh compliance packet for the
     * current tenant. Returns the presigned download URL, packet id, and —
     * for the official variant — the packet hash.
     *
     * @param options - Optional `{ variant }` (#2767). `official` (default)
     *   requires the Standard tier; `sample` is allowed on every tier and
     *   returns a per-page-watermarked preview without the packet hash.
     * @throws {StandardTierRequiredError} When the server returns 403
     *   `standard_tier_required` (Free tenant requesting the official packet).
     */
    static async generatePacket(options?: {
        variant?: CompliancePacketVariant;
    }): Promise<CompliancePacketResponse> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/compliance',
                options: { body: options?.variant ? { variant: options.variant } : {} },
            });
            const response = await restOperation.response;
            return await handleApiResponse<CompliancePacketResponse>(response);
        } catch (error) {
            if (statusCodeOf(error) === 403) {
                throw new StandardTierRequiredError();
            }
            console.error('Error generating compliance packet:', error);
            throw error;
        }
    }

    /**
     * Generate a compliance packet and trigger a browser download of the PDF.
     * Mirrors `PolicyAssignmentService.downloadCertificate`: fetch the
     * presigned URL, read the blob, and click a synthetic anchor.
     *
     * @param options - Optional `{ filename, variant }`. `filename` overrides
     *   the saved filename, which defaults to `compliance-packet-<YYYY-MM-DD>.pdf`
     *   (official) or `compliance-packet-sample-<YYYY-MM-DD>.pdf` (sample).
     *   `variant: 'sample'` (#2767) downloads the watermarked free-tier preview.
     * @throws {StandardTierRequiredError} When the tenant is below Standard
     *   and requests the official variant.
     */
    static async downloadCompliancePacket(options?: {
        filename?: string;
        variant?: CompliancePacketVariant;
    }): Promise<void> {
        const variant = options?.variant ?? 'official';
        const { url } = await CompliancePacketApiService.generatePacket({ variant });

        const fetchResponse = await fetch(url);
        if (!fetchResponse.ok) {
            throw new Error(`Failed to download compliance packet: ${fetchResponse.status}`);
        }
        const blob = await fetchResponse.blob();

        const datePart = new Date().toISOString().split('T')[0];
        const defaultName =
            variant === 'sample'
                ? `compliance-packet-sample-${datePart}.pdf`
                : `compliance-packet-${datePart}.pdf`;

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = options?.filename ?? defaultName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(objectUrl);
    }
}

/**
 * Settings → Compliance → "Share with auditor" admin API (#2496 Part 2).
 *
 * Admin endpoints (create / list / revoke) that mint and manage auditor share
 * links. The public viewer endpoint `GET /api/policy/auditor/:linkId` is
 * intentionally NOT on this class — the Amplify REST client attaches a Cognito
 * JWT to every request, and the public viewer must call from an unauthenticated
 * browser (raw fetch + `X-Aws-Account` header, lives in `pages/AuditorViewer`).
 *
 * Standard- or Premium-tier required (server-side enforced); a Free-tier 403
 * raises `StandardTierRequiredError` so the panel renders the inline upgrade
 * prompt rather than a generic failure message — mirrors `CompliancePacketApiService`.
 */
export class AuditorLinkApiService {
    /** Re-export so the UI layer can `instanceof`-check without importing twice. */
    static readonly StandardTierRequiredError = StandardTierRequiredError;

    /**
     * POST /api/auditor/links — mint a new auditor share link. The body is
     * always sent (empty object when no `expiresInDays` supplied) so the
     * controller sees an object it can `safeParse` cleanly.
     *
     * @param input - optional `{ expiresInDays }` (server caps at 90; default 30).
     * @returns `{ linkId, url, expiresAt }`.
     * @throws {StandardTierRequiredError} on HTTP 403.
     */
    static async createAuditorLink(input?: CreateAuditorLinkRequest): Promise<CreateAuditorLinkResponse> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: '/auditor/links',
                options: { body: input ?? {} },
            });
            const response = await restOperation.response;
            return await handleApiResponse<CreateAuditorLinkResponse>(response);
        } catch (error) {
            if (statusCodeOf(error) === 403) {
                throw new StandardTierRequiredError();
            }
            console.error('Error creating auditor link:', error);
            throw error;
        }
    }

    /**
     * GET /api/auditor/links — list the tenant's auditor share links, newest
     * first, with derived `status` (revoked > expired > active precedence).
     *
     * @returns array of {@link AuditorLinkSummary}.
     * @throws {StandardTierRequiredError} on HTTP 403.
     */
    static async listAuditorLinks(): Promise<AuditorLinkSummary[]> {
        try {
            const restOperation = get({
                apiName: API_NAME,
                path: '/auditor/links',
            });
            const response = await restOperation.response;
            return await handleApiResponse<AuditorLinkSummary[]>(response);
        } catch (error) {
            if (statusCodeOf(error) === 403) {
                throw new StandardTierRequiredError();
            }
            console.error('Error listing auditor links:', error);
            throw error;
        }
    }

    /**
     * POST /api/auditor/links/:linkId/revoke — soft-revoke a live link.
     * Idempotent: revoking an already-revoked link is a 204 success.
     *
     * @param linkId - the 43-character base64url link token.
     * @throws {StandardTierRequiredError} on HTTP 403.
     */
    static async revokeAuditorLink(linkId: string): Promise<void> {
        try {
            const restOperation = post({
                apiName: API_NAME,
                path: `/auditor/links/${linkId}/revoke`,
            });
            await restOperation.response;
        } catch (error) {
            if (statusCodeOf(error) === 403) {
                throw new StandardTierRequiredError();
            }
            console.error('Error revoking auditor link:', error);
            throw error;
        }
    }
}
